import {useEffect, useState} from 'react';
import {Card} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {Label} from '@/components/ui/label';
import {Input} from '@/components/ui/input';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Textarea} from '@/components/ui/textarea';
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog';
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover';
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from '@/components/ui/command';
import {toast} from '@/hooks/use-toast';
import {useLookupsSnapshot, toOptionLabel} from '@/features/lookups/snapshot';
import {adoptionWaitApi, AdoptionWaitPayload, AdoptionWaitRequest} from './api';
import {adoptionApi} from '@/features/adoption/api';
import type {AdopterProfile} from '@/features/adoption/types';
import {Bell, Check, ChevronsUpDown, Loader2, Pencil, Trash2} from 'lucide-react';
import {useSearchParams} from 'react-router-dom';

const NONE = '__none__';

const DELETE_REASONS = [
  {value: 'no_longer_wants_to_adopt', label: 'Ya no deseo adoptar'},
  {value: 'adopted_elsewhere', label: 'Ya adopté por otro medio'},
  {value: 'changed_preferences', label: 'Cambié mis preferencias'},
  {value: 'other', label: 'Otro'},
];

const emptyPayload: AdoptionWaitPayload = {
  speciesId: 0,
  sexId: null,
  sizeId: null,
  breedId: null,
  minAgeMonths: null,
  maxAgeMonths: null,
};

function label(item?: any) {
  return item ? toOptionLabel(item) : 'Cualquiera';
}

function cityStoredValue(profile?: AdopterProfile | null) {
  if (profile?.cityId) return String(profile.cityId);
  if (profile?.city_id) return String(profile.city_id);
  const city = profile?.city;
  if (!city) return '';
  if (typeof city === 'object') return city.id ? String(city.id) : '';
  return String(city);
}

function cityLabel(item?: any) {
  if (!item) return '';
  return item.label || [item.name, item.department].filter(Boolean).join(' - ') || item.name || String(item.id);
}

function monthsToYearsInput(months?: number | null) {
  if (months == null) return '';
  return String(Number((Number(months) / 12).toFixed(1)));
}

function yearsToMonths(value: string) {
  if (value === '') return null;
  return Math.round(Number(value) * 12);
}

function formatAgeMonths(months?: number | null) {
  if (months == null) return '';
  const normalized = Math.max(0, Number(months));
  const years = Math.floor(normalized / 12);
  const remainingMonths = normalized % 12;
  if (years && remainingMonths) return `${years} ${years === 1 ? 'año' : 'años'} y ${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`;
  if (years) return `${years} ${years === 1 ? 'año' : 'años'}`;
  return `${remainingMonths} ${remainingMonths === 1 ? 'mes' : 'meses'}`;
}

function ageLabel(request: AdoptionWaitRequest | AdoptionWaitPayload) {
  const min = request.minAgeMonths;
  const max = request.maxAgeMonths;
  if (min == null && max == null) return 'Cualquier edad';
  if (min != null && max != null) return `${formatAgeMonths(min)} a ${formatAgeMonths(max)}`;
  if (min != null) return `Desde ${formatAgeMonths(min)}`;
  return `Hasta ${formatAgeMonths(max)}`;
}

export function AdoptionWaitRequestCard() {
  const {data: lookups} = useLookupsSnapshot();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState<AdoptionWaitRequest | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<AdoptionWaitPayload>(emptyPayload);
  const [profile, setProfile] = useState<AdopterProfile | null>(null);
  const [city, setCity] = useState('');
  const [cityOpen, setCityOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState('changed_preferences');
  const [reasonText, setReasonText] = useState('');

  const species = lookups?.species || [];
  const sexes = lookups?.sexes || [];
  const sizes = lookups?.sizes || [];
  const cities = lookups?.cities || [];
  const profileCity = cityStoredValue(profile).trim();
  const selectedCity = cities.find((item: any) => String(item.id) === String(city));
  const selectedCityLabel = cityLabel(selectedCity) || city;

  const load = async () => {
    setLoading(true);
    try {
      const [current, currentProfile] = await Promise.all([
        adoptionWaitApi.getMine(),
        adoptionApi.getMyProfile(),
      ]);
      setRequest(current);
      setProfile(currentProfile);
      setCity(cityStoredValue(currentProfile));
      if (searchParams.get('waitRequest') === '1' && !current) setEditing(true);
      if (current) {
        setForm({
          speciesId: current.speciesId,
          sexId: current.sexId ?? null,
          sizeId: current.sizeId ?? null,
          breedId: null,
          minAgeMonths: current.minAgeMonths ?? null,
          maxAgeMonths: current.maxAgeMonths ?? null,
        });
      }
    } catch (error: any) {
      toast({variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudo cargar tu solicitud de espera.'});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.speciesId) {
      toast({variant: 'destructive', title: 'Validación', description: 'La especie es obligatoria.'});
      return;
    }
    if (form.minAgeMonths != null && form.maxAgeMonths != null && form.minAgeMonths > form.maxAgeMonths) {
      toast({variant: 'destructive', title: 'Validación', description: 'La edad mínima no puede ser mayor que la máxima.'});
      return;
    }
    if (!city.trim() || (cities.length > 0 && !selectedCity)) {
      toast({variant: 'destructive', title: 'Validación', description: 'Selecciona la ciudad donde resides.'});
      return;
    }
    setSaving(true);
    try {
      if (city.trim() !== profileCity) {
        await adoptionApi.upsertMyProfile({city: city.trim()});
      }
      const payload: AdoptionWaitPayload = {...form, breedId: null};
      const saved = request
        ? await adoptionWaitApi.updateMine(request.id, payload)
        : await adoptionWaitApi.createMine(payload);
      setRequest(saved);
      setEditing(false);
      toast({title: 'Solicitud guardada'});
      await load();
    } catch (error: any) {
      toast({variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudo guardar la solicitud.'});
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!request) return;
    setSaving(true);
    try {
      await adoptionWaitApi.deleteMine(request.id, {reasonCode, reasonText: reasonCode === 'other' ? reasonText : undefined});
      setRequest(null);
      setForm(emptyPayload);
      setDeleteOpen(false);
      setEditing(false);
      toast({title: 'Solicitud eliminada'});
    } catch (error: any) {
      toast({variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'No se pudo eliminar la solicitud.'});
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 space-y-5">
      <div className="flex items-start gap-3">
        <Bell className="h-5 w-5 text-primary mt-1" />
        <div className="flex-1">
          <h2 className="text-xl font-semibold">Alerta de preferencia de adopción</h2>
          <p className="text-sm text-muted-foreground">Si aún no encontraste tu compañero ideal, registra tus preferencias y te avisaremos por correo cuando haya un animal compatible.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando...</div>
      ) : !request && !editing ? (
        <Button onClick={() => setEditing(true)}>Crear solicitud</Button>
      ) : editing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Especie" value={form.speciesId} required items={species} onChange={(value) => setForm({...form, speciesId: Number(value), breedId: null})} />
            <SelectField label="Sexo" value={form.sexId} items={sexes} onChange={(value) => setForm({...form, sexId: value === NONE ? null : Number(value)})} />
            <SelectField label="Tamaño" value={form.sizeId} items={sizes} onChange={(value) => setForm({...form, sizeId: value === NONE ? null : Number(value)})} />
            <div className="space-y-2">
              <Label>Ciudad donde resides<span className="text-destructive ml-1">*</span></Label>
              <Popover open={cityOpen} onOpenChange={setCityOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="w-full justify-between font-normal">
                    <span className="truncate">{selectedCityLabel || 'Seleccionar ciudad'}</span>
                    <ChevronsUpDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput placeholder="Buscar ciudad..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron ciudades.</CommandEmpty>
                      <CommandGroup>
                        {cities.map((item: any) => {
                          const label = cityLabel(item);
                          return (
                            <CommandItem
                              key={item.id}
                              value={`${label} ${item.id}`}
                              onSelect={() => {
                                setCity(String(item.id));
                                setCityOpen(false);
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${String(city) === String(item.id) ? 'opacity-100' : 'opacity-0'}`} />
                              <span>{label}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Edad mínima (años)</Label>
              <Input type="number" min={0} step={0.5} value={monthsToYearsInput(form.minAgeMonths)} onChange={(e) => setForm({...form, minAgeMonths: yearsToMonths(e.target.value)})} />
            </div>
            <div className="space-y-2">
              <Label>Edad máxima (años)</Label>
              <Input type="number" min={0} step={0.5} value={monthsToYearsInput(form.maxAgeMonths)} onChange={(e) => setForm({...form, maxAgeMonths: yearsToMonths(e.target.value)})} />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Guardar solicitud</Button>
          </div>
        </div>
      ) : request ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Summary label="Especie" value={label(request.species)} />
            <Summary label="Sexo" value={label(request.sex)} />
            <Summary label="Tamaño" value={label(request.size)} />
            <Summary label="Edad deseada" value={ageLabel(request)} />
            <Summary label="Ciudad" value={selectedCityLabel || '-'} />
            <Summary label="Creada" value={request.createdAt ? new Date(request.createdAt).toLocaleDateString() : '-'} />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setEditing(true)}><Pencil className="h-4 w-4 mr-2" />Editar</Button>
            <Button variant="destructive" onClick={() => setDeleteOpen(true)}><Trash2 className="h-4 w-4 mr-2" />Eliminar solicitud</Button>
          </div>
        </div>
      ) : null}

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Eliminar solicitud</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Motivo</Label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DELETE_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {reasonCode === 'other' && (
              <div className="space-y-2">
                <Label>Detalle opcional</Label>
                <Textarea value={reasonText} onChange={(e) => setReasonText(e.target.value)} />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={remove} disabled={saving}>Eliminar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SelectField({label, value, items, required, onChange}: {label: string; value?: number | null; items: any[]; required?: boolean; onChange: (value: string) => void}) {
  return (
    <div className="space-y-2">
      <Label>{label}{required && <span className="text-destructive ml-1">*</span>}</Label>
      <Select value={value ? String(value) : NONE} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
        <SelectContent>
          {!required && <SelectItem value={NONE}>Cualquiera</SelectItem>}
          {items.map(item => <SelectItem key={item.id} value={String(item.id)}>{toOptionLabel(item)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Summary({label, value}: {label: string; value: string}) {
  return <div><p className="text-muted-foreground">{label}</p><p className="font-medium">{value}</p></div>;
}
